import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { TextField, Autocomplete, Typography, createFilterOptions, Skeleton } from "@mui/material";
import type { CommuneAssocieeDeleguee } from "models";
import { communesAssocieesDelegueesService } from "services";
import { useErrorHandler, withComponentErrorBoundary } from "utils/errorHandling";
import { stringUtil } from "@sdeapps/react-core";
import { grey } from "@mui/material/colors";

const filterOptions = createFilterOptions({
  trim: true,
  stringify: (option: CommuneAssocieeDeleguee) => {
    const normalizedString = stringUtil.normalize(option.libelle);
    return `${normalizedString} ${option.libelle}`;
  },
});

interface CommuneAssocieeDelegueeAutoCompleteProps {
  value: CommuneAssocieeDeleguee | null;
  onChange?: (communeAssocieeDeleguee: CommuneAssocieeDeleguee | null) => void;
  label?: string;
  placeholder?: string;
  size?: "small" | "medium";
  disabled?: boolean;
}

function CommuneAssocieeDelegueeAutoComplete({
  value,
  onChange,
  label,
  placeholder,
  size = "medium",
  disabled = false,
}: Readonly<CommuneAssocieeDelegueeAutoCompleteProps>): ReactNode {
  const [communesAssocieesDeleguees, setCommunesAssocieesDeleguees] = useState<
    Array<CommuneAssocieeDeleguee>
  >([]);
  const { catchErrors, isLoading } = useErrorHandler();

  useEffect(() => {
    async function getCommunes(): Promise<void> {
      const _communeAssocieeDeleguee = await communesAssocieesDelegueesService.getAll();
      setCommunesAssocieesDeleguees(_communeAssocieeDeleguee);
    }
    void catchErrors(getCommunes);
  }, [catchErrors]);

  return (
    <Autocomplete
      value={communesAssocieesDeleguees.find((c) => c.id === value?.id) ?? value ?? null}
      onChange={(_e, _value: CommuneAssocieeDeleguee | null) => {
        onChange?.(_value);
      }}
      options={communesAssocieesDeleguees}
      filterOptions={filterOptions}
      fullWidth
      autoHighlight
      openOnFocus
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      size={size}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={({ libelle }) => libelle}
      renderInput={(params) => <TextField {...params} label={label} placeholder={placeholder} />}
      renderOption={(props, cad) => (
        <Typography component="li" {...props} key={cad.id}>
          {cad.libelle} <span className="grey">({cad.id.substring(0, 2)})</span>
        </Typography>
      )}
      slotProps={{
        paper: { sx: { "&.MuiAutocomplete-paper li span.grey": { color: grey[500], ml: 0.5 } } },
      }}
      noOptionsText={isLoading ? <Skeleton /> : "Pas de résultat"}
      disabled={disabled}
    />
  );
}

export const CommuneAssocieeDelegueeAutoCompleteWithErrorBoundary = withComponentErrorBoundary(
  CommuneAssocieeDelegueeAutoComplete
);
